import { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import * as d3 from 'd3';
import './CoordinateSystem.css';
import { useTranslation } from 'react-i18next';
import {
  applyCanvasMasking,
  computeContours,
  renderContoursCanvas,
  drawPolygonsCanvas,
  drawMPLEsCanvas,
  drawPoints,
  drawCabinetBoxes,
  findMinMaxCoordinates,
  type CabinetBox,
} from 'src/utils/visualization';
import { useVisualizationScales, useContainerSize } from 'src/hooks';
import { useSelectedInstallation } from 'src/Contexts/SelectedInstallationContext';
import { useInstallationQuery } from 'src/queries/useInstallationsQuery';
import { formatLocalTimestamp } from 'src/utils/timestamps';
import type { Polygon, InterpolatedData } from 'src/types';

const gridResolution = 200;
const TARGET_CONTOUR_BANDS = 20;
const DEFAULT_ASPECT = 902 / 1204;
const MAX_HEIGHT_VH = 0.8;

export interface CoordinateSystemProps {
  points: any[];
  polygons: Polygon[];
  interpolatedData?: InterpolatedData | null;
  highlightedPoint?: any | null;
  onPointSelect?: (point: any | null) => void;
  selectedPoint?: any | null;
  // Optional background click handler. Receives installation-space (world)
  // coordinates recovered via xScale.invert / yScale.invert. When provided,
  // the cursor over the viz becomes a crosshair and clicks on empty space
  // (not on an existing point) fire this. Used by the admin manual-alert UI.
  onBackgroundClick?: (worldX: number, worldY: number) => void;
  // Optional draft marker rendered on top of the viz. Used to show the
  // pending focal point while the admin manual-alert form is open.
  focusPoint?: { x: number; y: number } | null;
  // Optional persistent markers — used by Status to show every active alert's
  // focal point on the live viz. Distinct color keeps them visually separate
  // from the admin's draft `focusPoint`.
  focusPoints?: Array<{ x: number; y: number; color?: string; title?: string }>;
  // Optional cabinet (Schaltschrank) regions to annotate — one dashed, labeled
  // box per cabinet that has an active alert. Drawn under the point layer.
  cabinetBoxes?: CabinetBox[];
  // Show the MPLE (electrode connecting-line) overlay. Per-installation admin
  // setting; default true.
  showMple?: boolean;
  // Show the interpolated color field. Per-installation admin setting; off
  // leaves only the colored points. Default true.
  showInterpolation?: boolean;
  // Electrode point circle radius in px. Per-installation admin setting.
  pointRadius?: number;
  // Which value the points/grid represent. 'voltage' (default) uses the raw
  // green→blue scale + configuration.color_scale_{min,max}; 'deviation' uses a
  // diverging scale centered on 0 + configuration.color_scale_dev_{min,max}.
  mode?: 'voltage' | 'deviation';
  // Live preview override of the color domain (e.g. the admin color-scale panel
  // dragging values before saving). Takes precedence over saved config + auto.
  colorOverride?: { min: number; max: number } | null;
}

function CoordinateSystem({
  points,
  polygons,
  interpolatedData,
  highlightedPoint = null,
  onPointSelect,
  selectedPoint = null,
  onBackgroundClick,
  focusPoint = null,
  focusPoints = [],
  cabinetBoxes = [],
  showMple = true,
  showInterpolation = true,
  pointRadius = 3.5,
  mode = 'voltage',
  colorOverride = null,
}: CoordinateSystemProps) {
  const { t } = useTranslation();

  // Shared zoom/pan transform, applied identically to the canvas layer (via
  // ctx.translate/scale) and every SVG layer (via a group transform) so the
  // contour field and the points/markers stay pixel-aligned while zooming.
  const [zoomTransform, setZoomTransform] = useState<d3.ZoomTransform>(() => d3.zoomIdentity);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  const handlePointClick = useCallback(
    (point: any) => {
      onPointSelect?.(point);
    },
    [onPointSelect]
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Callback ref: the ResizeObserver follows the actual mounted node, so the
  // measured width is correct even though this component renders the same
  // container on every state (no subtree swap that would strand the observer).
  const { ref: containerRef, size: containerSize } = useContainerSize();
  const containerWidth = containerSize.width;

  // Compute aspect ratio from data domain
  const domainAspect = useMemo(() => {
    // Prefer interpolated data domain (most accurate)
    if (interpolatedData?.x_domain && interpolatedData?.y_domain) {
      const dx = interpolatedData.x_domain[1] - interpolatedData.x_domain[0];
      const dy = interpolatedData.y_domain[1] - interpolatedData.y_domain[0];
      if (dx > 0 && dy > 0) return dy / dx;
    }
    // Fallback: compute from polygon/point bounds
    if (polygons.length > 0 || points.length > 0) {
      const domains = findMinMaxCoordinates(polygons, points);
      const dx = domains.xDomain[1] - domains.xDomain[0];
      const dy = domains.yDomain[1] - domains.yDomain[0];
      if (dx > 0 && dy > 0) return dy / dx;
    }
    return DEFAULT_ASPECT;
  }, [interpolatedData, polygons, points]);

  // Canvas dimensions from container width + domain aspect. Height MUST stay
  // width * domainAspect so the displayed box (aspect-ratio: width/height) and
  // the d3 scales agree — otherwise the data is stretched (a square roof like
  // GER2 rendered too tall/narrow). To bound very tall installations we shrink
  // *both* dimensions proportionally (reduce width), never clamp height alone.
  const fullWidth = containerWidth || 1204;
  const maxHeight = typeof window !== 'undefined' ? window.innerHeight * MAX_HEIGHT_VH : 800;
  const width =
    domainAspect > 0 && fullWidth * domainAspect > maxHeight
      ? Math.round(maxHeight / domainAspect)
      : fullWidth;
  const height = Math.round(width * domainAspect);

  const { scalesAndGrid, isLoading } = useVisualizationScales({
    points,
    polygons,
    width,
    height,
    gridResolution,
    interpolatedData,
  });

  // Per-installation color-scale override, per view mode. Voltage and deviation
  // keep separate saved pairs because their value ranges differ entirely.
  const { selectedInstallationID } = useSelectedInstallation();
  const { data: installation } = useInstallationQuery(selectedInstallationID ?? '');
  const cfg = installation?.configuration ?? {};
  const savedMin = mode === 'deviation' ? cfg.color_scale_dev_min : cfg.color_scale_min;
  const savedMax = mode === 'deviation' ? cfg.color_scale_dev_max : cfg.color_scale_max;

  // Color domain priority: live preview override > saved config > auto.
  // Voltage auto = p5/p95 of values. Deviation auto = symmetric ±p95(|z|) so 0
  // stays centered under the diverging scale. Fallbacks when no data yet.
  const colorDomain = useMemo((): [number, number] => {
    if (colorOverride && colorOverride.min < colorOverride.max) {
      return [colorOverride.min, colorOverride.max];
    }
    if (typeof savedMin === 'number' && typeof savedMax === 'number' && savedMin < savedMax) {
      return [savedMin, savedMax];
    }
    const values = points
      .map((p: any) => p._value)
      .filter((v: unknown): v is number => typeof v === 'number' && isFinite(v));
    if (mode === 'deviation') {
      if (values.length === 0) return [-3, 3];
      const absSorted = values.map((v) => Math.abs(v)).sort((a, b) => a - b);
      const p95 = absSorted[Math.floor(absSorted.length * 0.95)] || 1;
      return [-p95, p95];
    }
    if (values.length === 0) return [1, 12];
    const sorted = [...values].sort((a, b) => a - b);
    const p05 = sorted[Math.floor(sorted.length * 0.05)];
    const p95 = sorted[Math.floor(sorted.length * 0.95)];
    return p05 < p95 ? [p05, p95] : [sorted[0], sorted[sorted.length - 1]];
  }, [points, mode, savedMin, savedMax, colorOverride]);

  const colorScale = useMemo(() => {
    // Deviation: diverging scale over an ascending [min, max] domain. RdBu maps
    // 0→blue, 1→red; reverse it (1−t) so negative deviations read blue, positive
    // read red, and ~0 stays neutral (white) when the domain is symmetric.
    // Keeping the domain ascending leaves contour stepping + types unchanged.
    if (mode === 'deviation') {
      return d3
        .scaleSequential()
        .interpolator((t) => d3.interpolateRdBu(1 - t))
        .domain(colorDomain)
        .clamp(true);
    }
    return d3
      .scaleSequential()
      .interpolator(d3.interpolateRgb('rgb(0,255,0)', 'rgb(0,0,255)'))
      .domain(colorDomain)
      .clamp(true);
  }, [mode, colorDomain]);

  // Canvas effect: static layers (contours, polygons, MPLEs)
  useEffect(() => {
    const { xScale, yScale, gridValues, gridNx, gridNy } = scalesAndGrid;
    if (!xScale || !yScale || !gridValues || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    // Apply the shared zoom/pan transform so the canvas layers track the SVG
    // group transform exactly. Everything below draws in world→screen space via
    // the scales; the extra translate/scale here zooms that whole composition.
    ctx.translate(zoomTransform.x, zoomTransform.y);
    ctx.scale(zoomTransform.k, zoomTransform.k);

    // Skip contour rendering when there's no measurement data — drawing
    // contours over an all-zero IDW grid (empty points + polygons) produces
    // a misleading uniform-color wash. Polygons + MPLEs still render below.
    const hasMeasurementData = (points && points.length > 0) || !!interpolatedData?.grid_values;
    if (hasMeasurementData && showInterpolation) {
      const cRange = colorDomain[1] - colorDomain[0];
      const contourStep = cRange > 0 ? cRange / TARGET_CONTOUR_BANDS : 1.0;
      const contours = computeContours(gridValues, gridNx, gridNy, colorDomain, contourStep);

      // Pass original grid domain so the contour geo-transform uses the pipeline's
      // domain, not the aspect-adjusted xScale.domain() which may be wider/shifted.
      const gridDomain =
        interpolatedData?.x_domain && interpolatedData?.y_domain
          ? { x: interpolatedData.x_domain, y: interpolatedData.y_domain }
          : undefined;

      applyCanvasMasking(ctx, polygons, xScale, yScale, () => {
        renderContoursCanvas(ctx, contours, xScale, yScale, gridNx, gridNy, colorScale, gridDomain);
      });
    }

    drawPolygonsCanvas(ctx, polygons, xScale, yScale);
    if (showMple) drawMPLEsCanvas(ctx, points, xScale, yScale);
  }, [
    points,
    polygons,
    colorDomain,
    scalesAndGrid,
    colorScale,
    width,
    height,
    interpolatedData,
    zoomTransform,
    showMple,
    showInterpolation,
  ]);

  // SVG effect: interactive layers (points with click-to-select) + cabinet
  // alert boxes. All layers carry the same `zoomTransform` group transform so
  // they track the zoomed/panned canvas exactly.
  useEffect(() => {
    const { xScale, yScale } = scalesAndGrid;
    if (!xScale || !yScale) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('.coordinate-system-content').remove();

    const zt = zoomTransform.toString();

    // Full-size capture rect: always present so d3.zoom (wheel/drag) has a hit
    // target across the whole viz. When a background-click handler is wired it
    // also recovers installation-space coords (zoom-inverted, then scale-inverted)
    // and shows a crosshair for the admin manual-alert picker.
    const bg = svg
      .append('rect')
      .attr('class', 'coordinate-system-content coordinate-system-bg')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent')
      .style('pointer-events', 'all');
    if (onBackgroundClick) {
      bg.style('cursor', 'crosshair').on('click', function (event: MouseEvent) {
        const [px, py] = d3.pointer(event, this);
        const [zx, zy] = zoomTransform.invert([px, py]);
        onBackgroundClick(xScale.invert(zx), yScale.invert(zy));
      });
    }

    const pointsGroup = svg
      .append('g')
      .attr('class', 'coordinate-system-content')
      .attr('transform', zt)
      .style('pointer-events', 'auto');
    drawPoints(pointsGroup, points, xScale, yScale, colorScale, handlePointClick, pointRadius);

    // Cabinet boxes ON TOP of the points so the alert label/outline is never
    // hidden behind sensors. pointer-events:none + no opaque fill keeps the
    // points underneath visible and clickable.
    const boxGroup = svg
      .append('g')
      .attr('class', 'coordinate-system-content coordinate-system-boxes')
      .attr('transform', zt)
      .style('pointer-events', 'none');
    drawCabinetBoxes(boxGroup, cabinetBoxes, xScale, yScale, (nr) => t('status.cabinet', { nr }));
  }, [
    points,
    polygons,
    scalesAndGrid,
    colorScale,
    width,
    height,
    handlePointClick,
    onBackgroundClick,
    cabinetBoxes,
    zoomTransform,
    pointRadius,
    t,
  ]);

  // Attach the d3.zoom behavior once the SVG is mounted. Wheel zooms, drag
  // pans; double-click resets (default dblclick-zoom disabled). The behavior is
  // stored in a ref so the reset button can drive it programmatically.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const svg = d3.select<SVGSVGElement, unknown>(svgEl);

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 8])
      .on('zoom', (event) => setZoomTransform(event.transform));
    zoomBehaviorRef.current = zoom;

    svg.call(zoom).on('dblclick.zoom', null);
    svg.on('dblclick', () => {
      setZoomTransform(d3.zoomIdentity);
      svg.call(zoom.transform, d3.zoomIdentity);
    });

    return () => {
      svg.on('.zoom', null).on('dblclick', null);
    };
  }, []);

  const resetZoom = useCallback(() => {
    const svgEl = svgRef.current;
    const zoom = zoomBehaviorRef.current;
    // Reset React's transform state directly so the viz always snaps back, even
    // if the d3 zoom behavior's internal datum is momentarily out of sync with
    // this node. Then sync d3's own transform (without a transition, so the two
    // can't race) so the next wheel/drag gesture starts from identity.
    setZoomTransform(d3.zoomIdentity);
    if (svgEl && zoom) {
      d3.select<SVGSVGElement, unknown>(svgEl).call(zoom.transform, d3.zoomIdentity);
    }
  }, []);

  const isZoomed = zoomTransform.k !== 1 || zoomTransform.x !== 0 || zoomTransform.y !== 0;

  // Focus markers: persistent `focusPoints` (e.g. active alerts) and a
  // singular `focusPoint` (admin's draft). Both render on top of everything
  // and ignore pointer events so the background-click stays hittable through
  // them. Single effect keeps them on the same z-layer.
  useEffect(() => {
    const { xScale, yScale } = scalesAndGrid;
    if (!xScale || !yScale) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('.coordinate-system-focus').remove();

    if (!focusPoint && focusPoints.length === 0) return;

    const layer = svg
      .append('g')
      .attr('class', 'coordinate-system-focus')
      .attr('transform', zoomTransform.toString())
      .style('pointer-events', 'none');

    const drawMarker = (worldX: number, worldY: number, color: string, title?: string) => {
      const cx = xScale(worldX);
      const cy = yScale(worldY);
      const marker = layer.append('g');
      if (title) marker.append('title').text(title);
      marker
        .append('circle')
        .attr('cx', cx)
        .attr('cy', cy)
        .attr('r', 10)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2.5);
      marker.append('circle').attr('cx', cx).attr('cy', cy).attr('r', 3).attr('fill', color);
    };

    // Persistent markers first, draft last so it sits on top.
    for (const fp of focusPoints) {
      drawMarker(fp.x, fp.y, fp.color ?? 'red', fp.title);
    }
    if (focusPoint) {
      drawMarker(focusPoint.x, focusPoint.y, 'dodgerblue');
    }
  }, [focusPoint, focusPoints, scalesAndGrid, width, height, zoomTransform]);

  // Highlight effect: enlarge the selected/highlighted point and draw its pulse
  // ring. Depends on zoomTransform + scalesAndGrid too, because the main SVG
  // effect tears down and rebuilds the `.point` circles on every pan/zoom (and
  // on scale changes) — without those deps the highlight would be lost after a
  // pan and not reapplied until the selection itself changed.
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const circles = svg.selectAll<SVGCircleElement, any>('.point');

    // Reset all points to the configured base radius.
    circles.attr('r', pointRadius).classed('point--highlighted', false);
    svg.selectAll('.highlight-ring').remove();

    const active = selectedPoint || highlightedPoint;
    if (!active) return;

    // Scale the highlight + pulse ring from the base radius so a smaller base
    // still gets a proportional emphasis (2× enlarge, ~2.85× ring — matches the
    // original 3.5 → 7 → 10 proportions).
    circles
      .filter(
        (d: any) =>
          d.x === active.x &&
          d.y === active.y &&
          d.mple === active.mple &&
          d.abgriff === active.abgriff
      )
      .attr('r', pointRadius * 2)
      .classed('point--highlighted', true)
      .each(function () {
        const cx = d3.select(this).attr('cx');
        const cy = d3.select(this).attr('cy');
        const parent = d3.select(this.parentNode as Element);
        parent
          .insert('circle', ':first-child')
          .attr('class', 'highlight-ring')
          // Fixed base radius; the CSS pulse scales it (transform, not `r`) so
          // the animation composites instead of repainting the whole viz.
          .attr('cx', cx)
          .attr('cy', cy)
          .attr('r', pointRadius * 2.85)
          .attr('fill', 'none')
          .attr('stroke', 'red')
          .attr('stroke-width', 2)
          .attr('opacity', 0.7);
      });
  }, [highlightedPoint, selectedPoint, zoomTransform, scalesAndGrid, pointRadius]);

  if (isLoading) {
    return <div>Loading visualization...</div>;
  }

  // Render whenever there's something to draw: live data (points or a
  // server-computed grid), polygons (roof outline only — alert detail modal),
  // or marker-only overlays (focus point / active alerts on Status).
  const hasContent =
    (points && points.length > 0) ||
    !!interpolatedData?.grid_values ||
    polygons.length > 0 ||
    !!focusPoint ||
    focusPoints.length > 0;
  if (!hasContent) {
    return null;
  }

  // The container is ALWAYS rendered (never swapped for a placeholder) so the
  // ResizeObserver keeps measuring the same node. Until the width is known we
  // just hide the inner layers — the canvas/svg stay mounted, so the draw
  // effects fire as soon as the measured width arrives (no stranded blank viz).
  const measured = containerWidth > 0;

  return (
    <div ref={containerRef} className="coordinate-system-container">
      <div
        className="coordinate-system-layers"
        // max-width caps tall installations (height bound) without distorting;
        // aspect-ratio keeps the box matching the data so nothing stretches.
        style={{
          aspectRatio: `${width} / ${height}`,
          maxWidth: `${width}px`,
          visibility: measured ? 'visible' : 'hidden',
        }}
      >
        <canvas ref={canvasRef} className="coordinate-system-canvas" />
        <svg
          ref={svgRef}
          className="coordinate-system-svg"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
        />
        {isZoomed && (
          <button
            type="button"
            className="coordinate-system-zoom-reset"
            onClick={resetZoom}
            title={t('viz.resetZoom')}
            aria-label={t('viz.resetZoom')}
          >
            {t('viz.resetZoom')}
          </button>
        )}
      </div>

      {selectedPoint && (
        <div className="point-info-bar">
          <button
            className="point-info-bar-close"
            onClick={() => onPointSelect?.(null)}
            aria-label="Close"
          >
            &times;
          </button>
          <span>
            {t('viz.coordinates')}: ({Number(selectedPoint.x).toFixed(2)},{' '}
            {Number(selectedPoint.y).toFixed(2)})
          </span>
          <span>
            {t('viz.value')}: {Number(selectedPoint._value).toFixed(5)}
          </span>
          {selectedPoint.mple != null && (
            <span>
              {t('viz.mple')}: {Number(selectedPoint.mple).toFixed(0)}
            </span>
          )}
          {selectedPoint.karte != null && (
            <span>
              {t('viz.karte')}: {Number(selectedPoint.karte).toFixed(0)}
            </span>
          )}
          {selectedPoint.abgriff != null && (
            <span>
              {t('viz.abgriff')}: {Number(selectedPoint.abgriff).toFixed(0)}
            </span>
          )}
          {selectedPoint._time && (
            <span>
              {t('viz.timestamp')}:{' '}
              {formatLocalTimestamp(selectedPoint._time, 'sv-SE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default CoordinateSystem;
